import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import 'react-toastify/dist/ReactToastify.css';
import { ITransaction, IUpdateTransaction } from '../interface/transaction';
import { IAPIError } from '../interface/common';
import { useTranslation } from 'react-i18next';
import { API_URLS } from '../constants/apiUrl';
import { useLoading } from '../context/LoadingContext';
import { patch } from '../api/api';
import { showToast } from '../components/utils/toast';
import { useAnalytics } from './useAnalytics';
import { AnalyticsFeatureOperation } from '../interface/analytics';

const updateTransaction = async (
  transactionId: string,
  transactionData: IUpdateTransaction,
  params: Record<string, string>,
): Promise<ITransaction> => {
  return patch(`${API_URLS.UPDATE_TRANSACTION}${transactionId}`, transactionData, {
    params,
    skipGlobalErrorHandler: true,
  });
};

export const useUpdateTransaction = (): UseMutationResult<
  ITransaction,
  AxiosError<IAPIError>,
  { transactionId: string; transactionData: IUpdateTransaction; params: Record<string, string> }
> => {
  const { setIsLoading } = useLoading();
  const { t } = useTranslation();
  const { trackBusinessEvent } = useAnalytics();

  return useMutation({
    mutationFn: ({ transactionId, transactionData, params }) =>
      updateTransaction(transactionId, transactionData, params),
    onMutate: () => {
      setIsLoading(true);
    },
    onSuccess: () => {
      trackBusinessEvent(AnalyticsFeatureOperation.TRANSACTION_UPDATE);
      showToast('success', t('transaction:successfullyEdited'));
    },
    onSettled: () => {
      setIsLoading(false);
    },
  });
};
